import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import request from '../utils/request';

interface User {
  id: string;
  email: string;
  name: string;
  functionCodes: string[];
}

interface AuthState {
  // State
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Methods
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  checkAuth: () => boolean;
  setToken: (token: string) => void;
  fetchUserInfo: () => Promise<{ success: boolean; message?: string }>;
  initializeAuth: () => Promise<{ success: boolean; message?: string }>;
}

// Real admin login API call
// POST /grnt/admin/login
const adminLoginApi = async (
  email: string,
  password: string
): Promise<{ success: boolean; token?: string; user?: User; message?: string }> => {
  try {
    const response = await request.post('/grnt/admin/login', {
      email,
      password,
    });

    return {
      success: true,
      token: response.data.token,
      user: {
        id: String(response.data.user.id),
        email: response.data.user.email,
        name: response.data.user.name,
        functionCodes: response.data.user.function_codes || [],
      },
    };
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Login failed, please try again';
    return {
      success: false,
      message: errorMessage,
    };
  }
};

// Admin logout API call
// POST /gradmin/logout
const adminLogoutApi = async (): Promise<{ success: boolean; message?: string }> => {
  try {
    await request.post('/gradmin/logout');
    return { success: true };
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Logout failed';
    return { success: false, message: errorMessage };
  }
};

// Get current admin user info API call
// GET /gradmin/me
const adminGetMeApi = async (): Promise<{ success: boolean; user?: User; message?: string }> => {
  try {
    const response = await request.get('/gradmin/me');
    return {
      success: true,
      user: {
        id: String(response.data.id),
        email: response.data.email,
        name: response.data.name,
        functionCodes: response.data.function_codes || [],
      },
    };
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch user info';
    return { success: false, message: errorMessage };
  }
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true });

        try {
          const result = await adminLoginApi(email, password);

          if (result.success && result.token && result.user) {
            set({
              token: result.token,
              user: result.user,
              isAuthenticated: true,
              isLoading: false,
            });
            return { success: true };
          } else {
            set({ isLoading: false });
            return { success: false, message: result.message };
          }
        } catch {
          set({ isLoading: false });
          return { success: false, message: 'Login failed, please try again' };
        }
      },

      logout: async () => {
        // Call backend logout API to invalidate server-side token
        await adminLogoutApi();
        // Clear local state
        set({
          token: null,
          user: null,
          isAuthenticated: false,
        });
      },

      checkAuth: () => {
        const { token } = get();
        // Check if token exists and is valid
        if (!token) {
          return false;
        }
        // Additional token expiry check logic can be added here
        return true;
      },

      setToken: (token: string) => {
        set({
          token,
          isAuthenticated: true,
        });
      },

      fetchUserInfo: async () => {
        set({ isLoading: true });

        try {
          const result = await adminGetMeApi();

          if (result.success && result.user) {
            set({
              user: result.user,
              isAuthenticated: true,
              isLoading: false,
            });
            return { success: true };
          } else {
            // Failed to fetch user info, clear auth state
            set({
              token: null,
              user: null,
              isAuthenticated: false,
              isLoading: false,
            });
            return { success: false, message: result.message };
          }
        } catch {
          // Failed to fetch user info, clear auth state
          set({
            token: null,
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
          return { success: false, message: 'Failed to fetch user info, please try again' };
        }
      },

      initializeAuth: async () => {
        const { token, fetchUserInfo } = get();

        // If token exists, try to fetch latest user info
        if (isValidToken(token)) {
          const result = await fetchUserInfo();
          // If failed, state already cleared by fetchUserInfo - no extra handling needed
          return result;
        }
        return { success: false, message: 'Token is invalid' };
      },
    }),
    {
      name: 'auth-storage', // localStorage key
      partialize: state => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Helper function to validate token
export const isValidToken = (token: string | null): boolean => {
  if (!token) return false;
  // Token length check (backend generates 64-char random string)
  return token.length >= 64;
};

// Helper function: check if user has the specified function permission
export const hasFunction = (user: User | null, functionCode: string): boolean => {
  if (!user || !user.functionCodes) return false;
  return user.functionCodes.includes(functionCode);
};
